/**
 * Auth utilities for the Pieter Video Editor.
 *
 * Reads the shared Firebase Auth session from pieter-493314.
 * If the user is not signed in, shows a login screen inline
 * (Google OAuth popup runs on the editor origin so the session persists).
 *
 * Exports:
 *   useAuth()            -- React hook returning { user, loading, token, needsLogin }
 *   getAuthToken()       -- standalone async getter for the ID token
 *   signInWithGoogle()   -- trigger Google OAuth popup + approved-claim check
 */

"use client";

import { useState, useEffect } from "react";
import {
	onAuthStateChanged,
	signInWithPopup,
	signOut,
	GoogleAuthProvider,
	type User,
} from "firebase/auth";
import { auth } from "./firebase";

/** Get a fresh Firebase ID token, or null if not signed in. */
export async function getAuthToken(): Promise<string | null> {
	const user = auth.currentUser;
	if (!user) return null;
	try {
		return await user.getIdToken();
	} catch (err) {
		console.warn("[auth] getIdToken failed:", err);
		return null;
	}
}

export interface AuthState {
	user: User | null;
	loading: boolean;
	token: string | null;
	/** True when Firebase has finished loading and there is no session. */
	needsLogin: boolean;
}

/**
 * Sign in with Google OAuth popup and verify the approved custom claim.
 * Throws if the user is not in the allowed workspace.
 */
export async function signInWithGoogle(): Promise<User> {
	const provider = new GoogleAuthProvider();
	const result = await signInWithPopup(auth, provider);
	const tokenResult = await result.user.getIdTokenResult();
	const approved = tokenResult.claims["approved"];
	if (!approved) {
		const email = result.user.email;
		await signOut(auth);
		throw new Error(
			email
				? `Account ${email} is not in the allowed workspace.`
				: "Account is not in the allowed workspace.",
		);
	}
	return result.user;
}

/**
 * React hook that subscribes to Firebase auth state.
 * Returns { user, loading, token, needsLogin }.
 * When not authenticated, sets needsLogin=true so callers can render a login UI.
 */
export function useAuth({ redirect = true }: { redirect?: boolean } = {}): AuthState {
	const [state, setState] = useState<AuthState>({
		user: null,
		loading: true,
		token: null,
		needsLogin: false,
	});

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user) {
				try {
					const token = await user.getIdToken();
					setState({ user, loading: false, token, needsLogin: false });
				} catch {
					setState({ user, loading: false, token: null, needsLogin: false });
				}
			} else {
				setState({ user: null, loading: false, token: null, needsLogin: redirect });
			}
		});

		return () => unsubscribe();
	}, [redirect]);

	return state;
}
